/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"

/* Helpers, constants, custom methods, internal libraries */
import {
  TEAM_DETAILS_BOOKINGS_ROUTE,
  TEAM_DETAILS_ROUTE,
  TEAM_DETAILS_SETTINGS_ROUTE
} from "../../constants/routes";
import { useIsMobile } from "../../hooks/useIsMobile.js";
import { useHasPermission } from "../../hooks/useHasPermission";

/* Components */
import MembersTab from "./membersTab";
import BookingsTab from "./bookingsTab";
import SettingsTab from "./settingsTab";
import UCSelect from "../ucselect/index.js";

/* Styles */
import styles, { tabsSelectOpen } from "./styles.js"
import { commasFormat } from '../../helpers/general';
import { useTeamService } from '../../services/teamService';

const tabOptions = [];

const TeamDetailsTabbedNav = ({ teamDetails }) => {
  let { teamId } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { getTeamMembersCount } =  useTeamService({skipUseEffect: true});
  const { checkPermission } = useHasPermission();

  const [totalTabs, setTotalTabs] = useState({
    membersCount: "",
  });
  const [defaultPage, setDefaultPage] = useState("settings");

  useEffect(() => {
    setDefaultPage(getDefaultPage())
  }, [location.pathname]);

  useEffect(() => {
    if (checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB")) {
      if (tabOptions.findIndex((item) => item.value === "settings") === -1) {
        tabOptions.push({
          value: "settings",
          label: <FormattedMessage id="team.details.settings" />,
        });
      }
    }
    if (checkPermission("F_TEAMDASHBOARD_VIEWMEMBERSTAB")) {
      if (tabOptions.findIndex((item) => item.value === "members") === -1) {
        tabOptions.push({
          value: "members",
          label: <FormattedMessage id="team.details.members" />,
        });
      }
    }
    // { value: 'bookings', label: <FormattedMessage id="team.details.bookings" /> }
    const fetchCounts = async () => {
      const membersCount = await getTeamMembersCount(teamId);
      setTotalTabs(prev => ({
        ...prev,
        membersCount: membersCount || 0
      }));
    }
    fetchCounts();
  }, []);

  const getDefaultPage = () => {
    if(location.pathname.includes(TEAM_DETAILS_SETTINGS_ROUTE.replace(":teamId", teamId)) && checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB")) return "settings"
    if(location.pathname.includes(TEAM_DETAILS_ROUTE.replace(":teamId", teamId))) return "members"
    // if(location.pathname.includes(TEAM_DETAILS_BOOKINGS_ROUTE.replace(":teamId", teamId))) return "bookings"
  }

  const getDefaultLabel = () => {
    if(location.pathname.includes(TEAM_DETAILS_SETTINGS_ROUTE.replace(":teamId", teamId)) && checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB")) return <FormattedMessage id="team.details.settings"/>
    if(location.pathname.includes(TEAM_DETAILS_ROUTE.replace(":teamId", teamId))) return <FormattedMessage id="team.details.members"/>
    // if(location.pathname.includes(TEAM_DETAILS_BOOKINGS_ROUTE.replace(":teamId", teamId))) return <FormattedMessage id="team.details.bookings"/>
  }

  // useEffect(() => {
  //   const newTab = location.pathname.includes(TEAM_DETAILS_BOOKINGS_ROUTE.replace(":teamId", teamId)) ? "bookings" : "members";
  //   setActiveTab(newTab);
  // }, [location.pathname]);

  const handleTabChange = nextTab => {
    switch(nextTab) {
      case "settings":
        navigate('../' + TEAM_DETAILS_SETTINGS_ROUTE.replace(":teamId", teamId));
      break;
      case "members":
        navigate('../' + TEAM_DETAILS_ROUTE.replace(":teamId", teamId));
      break;
      // case "bookings":
      //   navigate('../' + TEAM_DETAILS_BOOKINGS_ROUTE.replace(":teamId", teamId));
      // break;
      default:
        break;
    }
  }

  const viewTab = tab => {
    switch(tab) {
      case "settings":
        if(checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB")) return <SettingsTab teamId={teamId}/> 
        else return
      case "members":
        if(checkPermission("F_TEAMDASHBOARD_VIEWMEMBERSTAB")) return <MembersTab teamId={teamId} teamDetails={teamDetails}/>
        else return
      // case "bookings":
      //   return <BookingsTab />
      default: break
    }
  }

  return (
    <div className={classes.peopleTabbedNav}>
     {isMobile ? (
        <>
          <UCSelect
            styles={tabsSelectOpen}
            defaultValue={{ 
              value: defaultPage || getDefaultPage(), 
              label: getDefaultLabel()
            }}
            value={defaultPage || getDefaultPage()}
            options={tabOptions}
            onChange={(tab) => handleTabChange(tab.value)}
          />
          {viewTab(defaultPage || getDefaultPage())}
        </>
      ) : (
        <Tabs
          onSelect={(nextTab) => handleTabChange(nextTab)}
          defaultActiveKey={getDefaultPage()}
          activeKey={defaultPage}
          id="team-details-page-tabs"
          className={classes.tabs}
        >
          {
            checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB") &&
              <Tab eventKey="settings" title={<FormattedMessage id="team.details.settings" />}>
                {getDefaultPage() === "settings" && <SettingsTab teamId={teamId} />}
              </Tab>
          }
          {
            checkPermission("F_TEAMDASHBOARD_VIEWMEMBERSTAB") &&
              <Tab eventKey="members" title={
                <>
                  <FormattedMessage id="team.details.members" />
                  {(totalTabs?.membersCount || totalTabs?.membersCount === 0) &&
                    <p className={classes.totalTeams}>
                      {commasFormat(totalTabs?.membersCount?.toString())}
                    </p>
                  }
                </>
              }>
                {getDefaultPage() === "members" && <MembersTab teamId={teamId} teamDetails={teamDetails}/>}
              </Tab>
          }
          {/* <Tab eventKey="bookings" title={<FormattedMessage id="team.details.bookings" />}>
            {getDefaultPage() === "bookings" && <BookingsTab />}
          </Tab> */}
        </Tabs>
      )}
    </div>
  )
}

export default TeamDetailsTabbedNav;
